import { useState, useEffect, useCallback } from 'react'
import { pointService } from '../services/pointService'
import type { Point, PointFilters } from '../types'

export function usePoints(initialFilters?: Partial<PointFilters>) {
  const [data, setData] = useState<Point[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<PointFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  })

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await pointService.getAll(filters)
      setData(response.data)
      setTotal(response.total_items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar puntos')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setPage = (page: number) => setFilters((prev) => ({ ...prev, page }))
  const setLimit = (limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))

  const setTransactionType = (transaction_type?: 'add' | 'spend') =>
    setFilters((prev) => ({ ...prev, transaction_type, page: 1 }))
  const setIsValid = (is_valid?: boolean) =>
    setFilters((prev) => ({ ...prev, is_valid, page: 1 }))
  const setDateFrom = (date_from?: string) =>
    setFilters((prev) => ({ ...prev, date_from, page: 1 }))
  const setDateTo = (date_to?: string) =>
    setFilters((prev) => ({ ...prev, date_to, page: 1 }))
  const setAmountMin = (amount_min?: number) =>
    setFilters((prev) => ({ ...prev, amount_min, page: 1 }))
  const setAmountMax = (amount_max?: number) =>
    setFilters((prev) => ({ ...prev, amount_max, page: 1 }))
  const setPointsMin = (points_min?: number) =>
    setFilters((prev) => ({ ...prev, points_min, page: 1 }))
  const setPointsMax = (points_max?: number) =>
    setFilters((prev) => ({ ...prev, points_max, page: 1 }))
  const setSearch = (search?: string) =>
    setFilters((prev) => ({ ...prev, search, page: 1 }))
  const setSorting = (sort_by?: string, sort_order?: 'asc' | 'desc') =>
    setFilters((prev) => ({ ...prev, sort_by, sort_order }))

  const applyFilters = (partial: Partial<PointFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: 1 }))

  const clearFilters = () =>
    setFilters({ page: 1, limit: filters.limit })

  const hasActiveFilters = !!(
    filters.transaction_type ||
    filters.is_valid !== undefined ||
    filters.date_from ||
    filters.date_to ||
    filters.amount_min !== undefined ||
    filters.amount_max !== undefined ||
    filters.points_min !== undefined ||
    filters.points_max !== undefined ||
    filters.search
  )

  return {
    data,
    total,
    isLoading,
    error,
    filters,
    hasActiveFilters,
    setPage,
    setLimit,
    setTransactionType,
    setIsValid,
    setDateFrom,
    setDateTo,
    setAmountMin,
    setAmountMax,
    setPointsMin,
    setPointsMax,
    setSearch,
    setSorting,
    applyFilters,
    clearFilters,
    refetch: fetchData,
  }
}
